/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField
} from "@mui/material";
import { ReactComponent as ArrowDown } from "assets/images/ArrowDown.svg";
import { ReactComponent as ClosedIcon } from "assets/images/CloseIcon.svg";
import WarningIcon from "assets/images/WarningAmberOutlined.svg";
import ErrorIcon from "assets/images/warning-2.svg";
import { CircularLoaderFullPage, SearchField } from "components/core";
import { Formik, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { callToCloudFunction, getDataFromFirestore } from "services/api/apiRequests";
import { CF_URLS } from "services/api/endpoints";
import {
  getJobTitles,
  getOperationalCenters
} from "services/api/query";
import { selectAuth } from "store/slices";
import spacing from "styles/spacing";
import {
  ALL_SELECTION,
  corporateAccessLevelDistrictId,
  ERROR_TYPES,
  VALUE_EMPTY_STRING,
  Y_CHECKER
} from "utils/constants";
import {
  dataDogErrorLog,
  findErrorMessage,
  getAuthenticatedUserBranch,
  getResponseData,
  getSortedData,
  handleRefId
} from "utils/helpers";
import EmployeeSearchPrintModal from "./EmployeeSearchPrintModal";
import { EmployeeSearchStyles } from "./EmployeeSearchStyles";
import EmployeeSearchTable from "./EmployeeSearchTable";
import { employeeSearchFormSchema, getEmailFieldError } from "./Schema";
const EmployeeSearchLayout = () => {
  const { t } = useTranslation();
  const districtId = getAuthenticatedUserBranch();
  const { currentBranch, auth } = useSelector(selectAuth);
  //form initial state
  const initialEmployeeSearch = {
    firstName: "",
    lastName: "",
    email: "",
    employeeId: "",
    activeFlag: false,
    district: t("common.all"),
    jobTitle: t("common.all"),
    defaultOperationalCenter: t("common.all"),
    emergencyContact: false,
    printType: t("employeeSearch.printEmergencyContact")
  };

  //form initialize using useFormik
  const employeeSearchForm = useFormik({
    initialValues: initialEmployeeSearch,
    validationSchema: employeeSearchFormSchema
  });

  const [loading, setLoading] = useState(false);
  const [loadingJobTitle, setLoadingJobTitle] = useState(false);
  const [loadingOperationalCenter, setLoadingOperationalCenter] =
    useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [disableFind, setDisableFind] = useState(false);
  const [isResetGrid, setIsResetGrid] = useState(true);
  const [warningMessage, setWarningMessage] = useState(null);
  const [warningAlertPopup, setWarningAlertPopup] = useState(false);
  const [throwError, setThrowError] = useState(false);
  const [throwErrorMessage, setThrowErrorMessage] = useState(null);
  const [isVisiblePrint, setIsVisiblePrint] = useState(false);

  //dropdown states
  const [districts, setDistricts] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [operationalCenter, setOperationalCenter] = useState([]);

  //dropdown default states
  const [districtsDef, setDistrictsDef] = useState(ALL_SELECTION);
  const [jobTitlesDef, setJobTitlesDef] = useState(ALL_SELECTION);
  const [operationalCenterDef, setOperationalCenterDef] =
    useState(ALL_SELECTION);

  //handle change dropdown
  const handleDropdownChange = () => {
    setSearchResults([]);
    setLoading(false);
    setThrowError(false);
    setIsResetGrid(true);
    setDisableFind(false);
    setWarningAlertPopup(false);
    setWarningMessage(null);
  };

  //handle clear
  const handleReset = () => {
    employeeSearchForm.resetForm();
    handleDropdownChange();
    setDistrictsDef(ALL_SELECTION);
    setJobTitlesDef(ALL_SELECTION);
    setOperationalCenterDef(ALL_SELECTION);
    setLoadingJobTitle(false);
    setLoadingOperationalCenter(false);
  };

  //get districts
  const fetchDistricts = async () => {
    try {
      setLoading(true);
      const reqBody = JSON.stringify({
        main_district_id: corporateAccessLevelDistrictId
      });
      const data = await getResponseData(
        reqBody,
        `${CF_URLS.employeeSearch.getLoadingValues}`,
        1
      );
      if (data?.data) {
        const districtArray = data?.data[0];
        const mappedDistrictsData = districtArray.map((res) => ({
          value: res.district_id,
          label: res.code.trim().toUpperCase()
        }));
        setDistricts(
          [].concat(
            [{ label: t("common.all") || "", value: t("common.all") || "" }],
            getSortedData(mappedDistrictsData, "label", "asc")
          )
        );
      } else if (data?.error) {
        setThrowErrorMessage(data.error);
        setThrowError(true);
      }
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(
        message,
        CF_URLS.employeeSearch.getLoadingValues,
        { error: e },
        refId
      );
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoading(false);
    }
  };

  //get job titles
  const fetchJobTitles = async () => {
    try {
      setLoadingJobTitle(true);
      const result = await getJobTitles(districtId);
      //set job titles to state
      const mappedJobTitleData = result.map(({ job_title_id, title }) => ({
        label: title.trim(),
        value: job_title_id
      }));

      const sortedData = mappedJobTitleData.sort((a, b) => {
        const fieldA = a.label.toLowerCase();
        const fieldB = b.label.toLowerCase();
        if (fieldA < fieldB) {
          return -1;
        }
        if (fieldA > fieldB) {
          return 1;
        }
        return 0;
      });
      setJobTitles(
        [].concat(
          [{ label: t("common.all") || "", value: t("common.all") || "" }],
          sortedData
        )
      );
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: e }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoadingJobTitle(false);
    }
  };

  //get job titles
  const fetchOperationalCenters = async () => {
    try {
      setLoadingOperationalCenter(true);
      const result = await getOperationalCenters(districtId);
      //set operational center to state
      const mappedOperationalCenterData = result.map(
        ({ name, operational_center_id }) => ({
          label: name.trim(),
          value: operational_center_id
        })
      );
      const sortedData = mappedOperationalCenterData.sort((a, b) => {
        const fieldA = a.label.toLowerCase();
        const fieldB = b.label.toLowerCase();
        if (fieldA < fieldB) {
          return -1;
        }
        if (fieldA > fieldB) {
          return 1;
        }
        return 0;
      });
      setOperationalCenter(
        [].concat(
          [{ label: t("common.all") || "", value: t("common.all") || "" }],
          sortedData
        )
      );
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: e }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoadingOperationalCenter(false);
    }
  };

  //fetch table data
  const fetchTableData = async (returnBody = false) => {
    try {
      setLoading(true);
      const reqBody = JSON.stringify({
        main_district_id: districtId,
        first_name: employeeSearchForm.values.firstName.toLowerCase(),
        last_name: employeeSearchForm.values.lastName,
        business_email_addr: employeeSearchForm.values.email.trim(),
        payroll_id: employeeSearchForm.values.employeeId.trim(),
        active_flag: employeeSearchForm.values.activeFlag
          ? Y_CHECKER
          : VALUE_EMPTY_STRING,
        district_id: employeeSearchForm.values.district === t("common.all") ? VALUE_EMPTY_STRING : employeeSearchForm.values.district,
        job_title_id: employeeSearchForm.values.jobTitle === t("common.all") ? VALUE_EMPTY_STRING : employeeSearchForm.values.jobTitle,
        time_type_id: VALUE_EMPTY_STRING,
        cost_center_id: VALUE_EMPTY_STRING,
        emergency_contact_flag: employeeSearchForm.values.emergencyContact
          ? Y_CHECKER
          : VALUE_EMPTY_STRING,
        filter_terminated_flag: VALUE_EMPTY_STRING,
        op_center_id: employeeSearchForm.values.defaultOperationalCenter === t("common.all") ? VALUE_EMPTY_STRING : employeeSearchForm.values.defaultOperationalCenter,
        work_center_id: VALUE_EMPTY_STRING,
        work_category_id: VALUE_EMPTY_STRING,
        supervisor_employee_id: VALUE_EMPTY_STRING,
        logged_in_employee_id: VALUE_EMPTY_STRING,
      });
      if (returnBody) {
        setLoading(false);
        return reqBody;
      }
      const response = await callToCloudFunction(
        reqBody,
        CF_URLS.employeeSearch.getSearchValues
      );
      const data = await getDataFromFirestore(response, 1, response.data.docId);
      const employeeSearchResult = data.data[0];
      let sortByLastName = employeeSearchResult.sort((a, b) => {
        const lastNameA = a.employee_name.split(",")[0].trim().toLowerCase();
        const lastNameB = b.employee_name.split(",")[0].trim().toLowerCase();
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        return 0;
      });
      setSearchResults(sortByLastName);
      setDisableFind(true);
      setIsResetGrid(false);
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: e }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setLoading(false);
    }
  };
  //handle find
  const handleFind = async () => {
    const emailError = getEmailFieldError(employeeSearchForm.values.email);
    employeeSearchForm.setFieldTouched("email", true, false);
    employeeSearchForm.setFieldError("email", emailError);
    if (emailError) {
      return;
    }
    await fetchTableData();
  };

  //handle security group change
  const handleDistrictChange = (event, value) => {
    handleDropdownChange();
    setDistrictsDef(value);
    employeeSearchForm.handleChange({
      target: { name: "district", value: value?.value }
    });
  };

  //handle job title change
  const handleJobTitleChange = (event, value) => {
    handleDropdownChange();
    setJobTitlesDef(value);
    employeeSearchForm.handleChange({
      target: { name: "jobTitle", value: value?.value }
    });
  };

  //handle operational center change
  const handleOperationalCenterChange = (event, value) => {
    handleDropdownChange();
    setOperationalCenterDef(value);
    employeeSearchForm.handleChange({
      target: { name: "defaultOperationalCenter", value: value?.value }
    });
  };

  //handle first name change
  const handleFirstName = (event, value) => {
    handleDropdownChange();
    employeeSearchForm.handleChange({
      target: { name: "firstName", value: event.target.value }
    });
  };
  //handle last name change
  const handleLastName = (event, value) => {
    handleDropdownChange();
    employeeSearchForm.handleChange({
      target: { name: "lastName", value: event.target.value }
    });
  };
  //handle email change (validate on blur only)
  const handleEmail = (event) => {
    handleDropdownChange();
    employeeSearchForm.setFieldValue("email", event.target.value, false);
    if (employeeSearchForm.errors.email) {
      employeeSearchForm.setFieldError("email", undefined);
    }
  };
  const handleEmailBlur = (event) => {
    const value = event.target.value;
    employeeSearchForm.setFieldTouched("email", true, false);
    employeeSearchForm.setFieldError("email", getEmailFieldError(value));
  };
  //handle employee id (payroll) change
  const handleEmployeeId = (event) => {
    handleDropdownChange();
    employeeSearchForm.handleChange({
      target: { name: "employeeId", value: event.target.value }
    });
  };
  useEffect(() => {
    if (districtId) {
      fetchDistricts();
      fetchJobTitles();
      fetchOperationalCenters();
    }
  }, [districtId]);

  return (
    <>
      {/*Circular loader*/}
      <CircularLoaderFullPage loading={loading} />
      {/*error alert box*/}
      {throwError && (
        <Collapse in={throwError}>
          <Alert
            severity="error"
            icon={<img src={ErrorIcon} alt="error" />}
            sx={EmployeeSearchStyles.alertBox}
            action={
              <IconButton
                aria-label={t("common.close")}
                color="inherit"
                size="small"
                onClick={() => {
                  setThrowError(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {throwErrorMessage}
          </Alert>
        </Collapse>
      )}
      {/*Warnning message popup*/}
      <Collapse in={warningAlertPopup}>
        <Alert
          severity="warning"
          icon={<img src={WarningIcon} alt="warning" />}
          sx={EmployeeSearchStyles.alertBox}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setWarningAlertPopup(false)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {warningMessage}
        </Alert>
      </Collapse>
      <Formik>
        <form>
          <Grid
            display={"flex"}
            flexDirection={"column"}
            gap={spacing.verticalMargin20}
          >
            <Grid container spacing={spacing.verticalMargin20}>
              {/*First Name field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <TextField
                  id="first-name"
                  name="firstName"
                  label={t("employeeSearch.firstName")}
                  size="medium"
                  variant="outlined"
                  value={employeeSearchForm.values.firstName}
                  onChange={handleFirstName}
                  error={Boolean(employeeSearchForm.errors.firstName)}
                  helperText={employeeSearchForm.errors.firstName}
                  fullWidth
                />
              </Grid>

              {/*Last Name field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <TextField
                  id="last-name"
                  name="lastName"
                  label={t("employeeSearch.lastName")}
                  size="medium"
                  variant="outlined"
                  value={employeeSearchForm.values.lastName}
                  onChange={handleLastName}
                  error={Boolean(employeeSearchForm.errors.lastName)}
                  helperText={employeeSearchForm.errors.lastName}
                  fullWidth
                />
              </Grid>

              {/*Email field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <TextField
                  id="email"
                  name="email"
                  label={t("employeeSearch.email")}
                  size="medium"
                  variant="outlined"
                  value={employeeSearchForm.values.email}
                  onChange={handleEmail}
                  onBlur={handleEmailBlur}
                  error={
                    employeeSearchForm.touched.email &&
                    Boolean(employeeSearchForm.errors.email)
                  }
                  helperText={
                    employeeSearchForm.touched.email &&
                    employeeSearchForm.errors.email
                  }
                  inputProps={{ maxLength: 100 }}
                  fullWidth
                />
              </Grid>

              {/*Employee ID (Payroll ID) field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <TextField
                  id="employee-id"
                  name="employeeId"
                  label={t("employeeSearch.employeeId")}
                  size="medium"
                  variant="outlined"
                  value={employeeSearchForm.values.employeeId}
                  onChange={handleEmployeeId}
                  error={Boolean(employeeSearchForm.errors.employeeId)}
                  helperText={employeeSearchForm.errors.employeeId}
                  inputProps={{ maxLength: 30 }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3} xl={3}>
                {/*District search field*/}
                <SearchField
                  label={t("employeeSearch.district")}
                  id="district-search"
                  options={districts}
                  clearIcon={<ClosedIcon />}
                  popupIcon={<ArrowDown />}
                  value={districtsDef}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleDistrictChange}
                  error={!!employeeSearchForm.errors.district}
                  helperText={employeeSearchForm.errors.district}
                  required
                />
              </Grid>

              {/*Job title search field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <SearchField
                  label={t("employeeSearch.jobTitle")}
                  id="job-title-search"
                  options={jobTitles}
                  clearIcon={<ClosedIcon />}
                  popupIcon={<ArrowDown />}
                  value={jobTitlesDef}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleJobTitleChange}
                  error={!!employeeSearchForm.errors.securityGroup}
                  helperText={employeeSearchForm.errors.securityGroup}
                  loading={loadingJobTitle}
                  required
                />
              </Grid>

              {/*Default Operational Center search field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <SearchField
                  label={t("employeeSearch.defaultOperationalCenter")}
                  id="default-operational-center-search"
                  options={operationalCenter}
                  clearIcon={<ClosedIcon />}
                  popupIcon={<ArrowDown />}
                  value={operationalCenterDef}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleOperationalCenterChange}
                  error={!!employeeSearchForm.errors.defaultOperationalCenter}
                  helperText={
                    employeeSearchForm.errors.defaultOperationalCenter
                  }
                  loading={loadingOperationalCenter}
                  required
                />
              </Grid>
              {/*Active flag field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <FormControlLabel
                  id="activeFlag"
                  control={
                    <Checkbox
                      id="activeFlag"
                      size="small"
                      color="primary"
                      checked={employeeSearchForm.values.activeFlag}
                      onChange={(event) => {
                        employeeSearchForm.setFieldValue(
                          "activeFlag",
                          event.target.checked
                        );
                        handleDropdownChange();
                      }}
                    />
                  }
                  label={t("employeeSearch.activeFlag")}
                />
              </Grid>

              {/*Emergency Contact field*/}
              <Grid item xs={12} md={6} lg={3} xl={3}>
                <FormControlLabel
                  id="emergencyContact"
                  control={
                    <Checkbox
                      id="emergencyContact"
                      size="small"
                      color="primary"
                      checked={employeeSearchForm.values.emergencyContact}
                      onChange={(event) => {
                        employeeSearchForm.setFieldValue(
                          "emergencyContact",
                          event.target.checked
                        );
                        handleDropdownChange();
                      }}
                    />
                  }
                  label={t("employeeSearch.emergencyContact")}
                />
              </Grid>
            </Grid>
            <Stack direction="row" spacing={spacing.verticalMargin20}>
              {/*Clear button*/}

              <Button
                id="security-group-clear"
                variant="outlined"
                onClick={handleReset}
              >
                {t("common.clear")}
              </Button>
              {/*Find button*/}
              <Button
                id="security-group-find"
                variant="contained"
                type="button"
                onClick={handleFind}
                disabled={
                  disableFind ||
                  !employeeSearchForm.values.district ||
                  !employeeSearchForm.values.jobTitle ||
                  !employeeSearchForm.values.defaultOperationalCenter ||
                  Boolean(employeeSearchForm.errors.firstName) ||
                  Boolean(employeeSearchForm.errors.lastName) ||
                  (employeeSearchForm.touched.email &&
                    Boolean(employeeSearchForm.errors.email)) ||
                  Boolean(employeeSearchForm.errors.employeeId)
                }
              >
                {t("common.find")}
              </Button>
            </Stack>

            <EmployeeSearchTable
              searchResults={searchResults}
              isResetGrid={isResetGrid}
              setIsVisiblePrint={setIsVisiblePrint}
              jobTitle={jobTitles}
              setWarningAlertPopup={setWarningAlertPopup}
              setWarningMessage={setWarningMessage}
              setLoading={setLoading}
              setThrowErrorMessage={setThrowErrorMessage}
              setThrowError={setThrowError}
            />
            <EmployeeSearchPrintModal
              getFilterBody={fetchTableData}
              employeeSearchForm={employeeSearchForm}
              isVisiblePrint={isVisiblePrint}
              setIsVisiblePrint={setIsVisiblePrint}
              setLoading={setLoading}
            />
          </Grid>
        </form>
      </Formik>
    </>
  );
};

export default EmployeeSearchLayout;
